// eslint-disable-next-line no-unused-vars
import React from 'react';
import { array, bool, number, func, oneOfType, string } from 'prop-types';
import {
  ComposedModal, ModalHeader, Loading, ModalBody, Button
} from '@carbon/react';
import { Download } from 'lucide-react';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';

const StyledComposedModal = styled(ComposedModal)`
  .cds--modal-container {
    max-width: 550px;
  }

  .cds--modal-header {
    margin-bottom: 0;
  }

  .cds--modal-content {
    margin: 0;
    padding: 15px 1rem 20px;
    position: relative;

    .cds--loading-overlay {
      position: absolute;
      background-color: rgba(22, 22, 22, 0.15) !important;
      z-index: 1;
    }
  }

  .notificationBody {
    margin: 25px 0;

    &:not(:last-of-type) {
      padding-bottom: 10px;
      margin-bottom: 10px;
    }

    .notificationHeader {
      padding-bottom: 12px;
      margin-bottom: 12px;
      border-bottom: 1px solid #E2E2E2;

      .title {
        margin-bottom: 5px;
      }

      .date {
        color: #5F5F60;
      }
    }

    p {
      padding-right: 0;
      white-space: pre-line;
    }

    .buttons {
      margin: 15px 0 5px;
      display: flex;
      justify-content: flex-start;

      a, button {
        display: flex;
        text-align: center;
        height: 35px;
        min-height: 0;
        margin-right: 10px;
        padding: 0 30px;

        svg {
          margin-right: 8px;
        }
      }
    }
  }

  @media screen and (max-width: 768px) {
    .notificationBody {
      .buttons {
        flex-direction: column;
        gap: 10px;

        a, button {
          min-width: 100%;
        }
      }
    }
  }
`;

const AlertsModal = ({ notifications, onClose, loadingNotifications, open, onRead, loading, handleFileDownload }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  return (
    <StyledComposedModal
      open={ open }
      className="AlertsModal"
      size="md"
      theme={ theme }
      onClose={ onClose }
      selectorPrimaryFocus=".cds--modal-content"
    >
      <ModalHeader>
        <h4>{ t('alerts.latestNotifications') }</h4>
      </ModalHeader>
      <ModalBody>
        { loadingNotifications && <Loading withOverlay active /> }
        { notifications.map(notification => (
          <div className="notificationBody" key={ notification.messageId }>
            <div className="notificationHeader">
              <h5 className="title">{ notification.title }</h5>
              <span className="date">{ notification.startDate }</span>
            </div>
            <p dangerouslySetInnerHTML={{ __html: notification.content }} />
            <div className="buttons">
              <Button
                onClick={ () => onRead(notification.messageId) }
                data-test="onReadNotification"
                data-testid={ notification.messageId }
                kind="primary"
                className="red"
              >
                { loading === notification.messageId && (
                  <Loading withOverlay={ false } small active />
                ) }
                { t('notifications.markAsRead') }
              </Button>
              { notification.filename && (
                <button
                  type="button"
                  className="cds--btn cds--btn--ghost blue"
                  onClick={ () => handleFileDownload(notification.messageId) }
                >
                  <Download />
                  { t('common.downloadAttachment') }
                </button>
              ) }
            </div>
          </div>
        )) }
      </ModalBody>
    </StyledComposedModal>
  );
};

AlertsModal.displayName = 'AlertsModalComponent';

AlertsModal.propTypes = {
  notifications: array,
  onClose: func,
  onRead: func,
  loading: oneOfType([bool, string, number]),
  open: bool,
  loadingNotifications: bool,
  handleFileDownload: func,
};

export default AlertsModal;
